/**
 * OT Room config API (B2 / S6–S7) — operation-theatre room masters.
 *
 * Backend endpoints (org from session; behind B1's `require_admission_enabled`
 * gate + `MANAGE_BED_CONFIG`):
 *
 *   POST   /api/organisation/ot-rooms
 *   GET    /api/organisation/ot-rooms?include_inactive=
 *   GET    /api/organisation/ot-rooms/:ot_room_id
 *   PATCH  /api/organisation/ot-rooms/:ot_room_id
 *   DELETE /api/organisation/ot-rooms/:ot_room_id   (soft delete)
 *
 * Clone of bedCategoryApi.js (org-global; NO presets — rooms are
 * hospital-specific). Every function returns `{ success, data?, error?, code? }`.
 */

import apiClient from './apiClient';

const BASE = '/api/organisation/ot-rooms';

/** Local clone of the catalog error translator (see bedCategoryApi.js). */
function parseError(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.code) {
    return { error: detail.message || fallback, code: detail.code };
  }
  if (typeof detail === 'string') return { error: detail };

  switch (error?.response?.status) {
    case 400: return { error: 'Invalid data — please check the form and try again.' };
    case 401: return { error: 'Session expired. Please login again.' };
    case 403: return { error: 'You do not have permission to perform this action.' };
    case 404: return { error: 'The requested item could not be found.' };
    case 409: return { error: 'That item already exists. Pick a different name.' };
    case 422: return { error: 'Invalid data — please check the form and try again.' };
    case 500: return { error: 'Server error. Please try again in a moment.' };
    default:  return { error: fallback };
  }
}

/** POST /api/organisation/ot-rooms → 201 OTRoomResponse */
export const createOTRoom = async (body) => {
  try {
    const response = await apiClient.post(BASE, body);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[OTRoomAPI] create error:', error);
    return { success: false, ...parseError(error, 'Failed to create OT room.') };
  }
};

/** GET /api/organisation/ot-rooms → {ot_rooms:[...], count} (no preset seed) */
export const listOTRooms = async ({ include_inactive } = {}) => {
  try {
    const params = {};
    if (include_inactive) params.include_inactive = true;
    const response = await apiClient.get(BASE, { params });
    return {
      success: true,
      data: {
        ot_rooms: response.data?.ot_rooms || [],
        count: response.data?.count ?? (response.data?.ot_rooms?.length || 0),
      },
    };
  } catch (error) {
    console.error('[OTRoomAPI] list error:', error);
    return { success: false, ...parseError(error, 'Failed to load OT rooms.') };
  }
};

/** GET /api/organisation/ot-rooms/:id */
export const getOTRoom = async (ot_room_id) => {
  try {
    const response = await apiClient.get(`${BASE}/${encodeURIComponent(ot_room_id)}`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[OTRoomAPI] get error:', error);
    return { success: false, ...parseError(error, 'Failed to load OT room.') };
  }
};

/** PATCH /api/organisation/ot-rooms/:id — partial update. */
export const updateOTRoom = async (ot_room_id, body) => {
  try {
    const response = await apiClient.patch(`${BASE}/${encodeURIComponent(ot_room_id)}`, body);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[OTRoomAPI] update error:', error);
    return { success: false, ...parseError(error, 'Failed to update OT room.') };
  }
};

/**
 * B5 / S3 — tag an OT room with 0..N departments ("Visible to"). GROUPING HINT
 * ONLY: OT day-boards always list every room (no department_id param), so a tag
 * never hides a theatre. Empty array clears all edges → owner_scope reverts to
 * ORG_WIDE; ≥1 → owner_scope=DEPARTMENT. Server rejects (422 `{code, message}`)
 * an unknown (DEPARTMENT_NOT_FOUND) / inactive (DEPARTMENT_INACTIVE) / racing
 * (CONCURRENT_MODIFICATION) department. Response = enriched OTRoomResponse.
 *
 * PUT /api/organisation/ot-rooms/:id/departments
 * @param {string}   ot_room_id
 * @param {string[]} department_ids
 * @returns {Promise<{success, data?, error?, code?}>}
 */
export const setOTRoomDepartments = async (ot_room_id, department_ids) => {
  try {
    const response = await apiClient.put(
      `${BASE}/${encodeURIComponent(ot_room_id)}/departments`,
      { department_ids: department_ids || [] }
    );
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[OTRoomAPI] setDepartments error:', error);
    return { success: false, ...parseError(error, 'Failed to save departments.') };
  }
};

/** DELETE /api/organisation/ot-rooms/:id → 200 (soft-delete, active=false) */
export const deactivateOTRoom = async (ot_room_id) => {
  try {
    const response = await apiClient.delete(`${BASE}/${encodeURIComponent(ot_room_id)}`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[OTRoomAPI] deactivate error:', error);
    return { success: false, ...parseError(error, 'Failed to deactivate OT room.') };
  }
};
